import { useState, useEffect, useMemo } from "react";
import { Col, Form } from "react-bootstrap";
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';
import AnimatedSelect from "../../../shared/AnimatedSelect";
import "../../../shared/AnimatedSelect.css";

const PartnerLeadFilters = ({ filterValues = {}, campaign, journeys, lists, features, landingpage,leadstage, region, state, city, onFilterChange }) => {
  const [selectedFilter, setSelectedFilter] = useState(filterValues.mainFilter || "");
  const [subFilter, setSubFilter] = useState(filterValues.subFilter || "");

  // Keep local UI state in sync when parent provides prefilled values
  useEffect(() => {
    setSelectedFilter(filterValues.mainFilter || "");
  }, [filterValues.mainFilter]);

  useEffect(() => {
    setSubFilter(filterValues.subFilter || "");
  }, [filterValues.subFilter]);

  // Ensure default subFilter for Lead Timeline is 'Created On' so calendar shows by default
  useEffect(() => {
    if (selectedFilter === "Lead Timeline" && !subFilter) {
      setSubFilter("Created On");
      onFilterChange && onFilterChange("subFilter", "Created On");
    }
  }, [selectedFilter, subFilter, onFilterChange]);

  // Build Lead Stage options from dynamic dropdowns (value = id, label = name)
  const leadStageOptions = useMemo(() => {
    const source = Array.isArray(leadstage) ? leadstage : [];
    return source
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") {
          return { label: item, value: item };
        }
        const label = item.name || item.label || item.title || item.stage || item.status || String(item.id ?? item);
        const value = item.id ?? item.value ?? label;
        return { label, value: String(value) };
      })
      .filter(Boolean);
  }, [leadstage]);

  // Options for each main filter
  const filterOptions = useMemo(() => ({
    "Lead Source": [
      "Website Form",
      "Landing Pages",
      "Campaign",
      "Manual Entry",
    ],
    "Lead Status / Stage": leadStageOptions,
    "Lead Demographics": [],
    "Lead Timeline": ['Created On','Last Updated On','Converted On'],
  }), [leadStageOptions]);

  // Dynamic placeholder for sub-filter based on the selected main filter
  const subFilterPlaceholder = useMemo(() => {
    if (selectedFilter === "Lead Source") return "Select source";
    if (selectedFilter === "Lead Status / Stage") return "Select status/stage";
    if (selectedFilter === "Lead Timeline") return "Select timeline field";
    return "Select related field";
  }, [selectedFilter]);

  // If a previously selected main filter no longer exists (e.g., removed), reset safely
  useEffect(() => {
    if (selectedFilter && !Object.prototype.hasOwnProperty.call(filterOptions, selectedFilter)) {
      setSelectedFilter("");
      setSubFilter("");
      onFilterChange && onFilterChange("mainFilter", "");
      onFilterChange && onFilterChange("subFilter", "");
    }
  }, [selectedFilter, onFilterChange, filterOptions]);

  // Sub-options for Campaign and Landing Pages
  const campaignOptions = ["Email", "WhatsApp", "Push"];
  //const landingPages = ["Page 1", "Page 2", "Page 3"];


  return (
    <>
      {/* Main Filter Select */}
      <Col className="custom_col">
        <AnimatedSelect
          value={filterValues.hasDone || "HasDone"}
          options={[
            { label: "Has Done", value: "HasDone" },
            { label: "Has Not Done", value: "HasNotDone" }
          ]}
          onChange={(val) => onFilterChange && onFilterChange("hasDone", val)}
        />
      </Col>

      <Col className="custom_col">
        <AnimatedSelect
          value={selectedFilter}
          placeholder="Select Leads By"
          options={Object.keys(filterOptions).map(key => ({
            label: key,
            value: key
          }))}
          onChange={(val) => {
            setSelectedFilter(val);
            // Set default subFilter for Lead Timeline
            if (val === "Lead Timeline") {
              setSubFilter("Created On");
              onFilterChange && onFilterChange("subFilter", "Created On");
            } else {
              setSubFilter("");
              onFilterChange && onFilterChange("subFilter", "");
            }
            onFilterChange && onFilterChange("mainFilter", val);
          }}
        />
      </Col>

      {/* Sub Filter Select */}
      {selectedFilter && (filterOptions[selectedFilter] || []).length > 0 && (
        <Col className="custom_col">
          <AnimatedSelect
            value={subFilter}
            placeholder={subFilterPlaceholder}
            options={(filterOptions[selectedFilter] || []).map((option) => {
              if (option && typeof option === 'object' && (option.label !== undefined && option.value !== undefined)) {
                return option;
              }
              const label = typeof option === 'string' ? option : String(option);
              return { label, value: label };
            })}
            onChange={(val) => {
              setSubFilter(val);
              onFilterChange && onFilterChange("subFilter", val);
            }}
          />
        </Col>
      )}

      {/* Dynamic Input Fields Based on Selection */}
      {selectedFilter === "Lead Source" && subFilter === "Campaign" && (
        <Col className="custom_col">
          <AnimatedSelect
            value={filterValues.campaignType || ""}
            placeholder="Select Campaign Type"
            options={campaignOptions.map(option => ({
              label: option,
              value: option
            }))}
            onChange={(val) => onFilterChange && onFilterChange("campaignType", val)}
          />
        </Col>
      )}

      {selectedFilter === "Lead Source" && subFilter === "Landing Pages" && (
        <Col className="custom_col">
          <AnimatedSelect
            value={filterValues.landingPage || ""}
            placeholder="Select Landing Page"
            options={landingpage.map(page => ({ label: page.name, value: page.id }))}
            onChange={(val) => onFilterChange && onFilterChange("landingPage", val)}
          />
        </Col>
      )}

      {selectedFilter === "Lead Demographics" && (
        <>
          <Col className="custom_col">
            <AnimatedSelect
              value={filterValues.leadZone || ""}
              placeholder="Select Zone"
              options={region.map(item => ({ label: item.name, value: item.id }))}
              onChange={(val) => {
                onFilterChange && onFilterChange("leadZone", val);
                // Reset state and city when zone changes
                onFilterChange && onFilterChange("leadState", "");
                onFilterChange && onFilterChange("leadCity", "");
              }}
            />
          </Col>
          <Col className="custom_col">
            <AnimatedSelect
              value={filterValues.leadState || ""}
              placeholder="Select State"
              options={state.map(item => ({ label: item.name, value: item.id }))}
              onChange={(val) => {
                onFilterChange && onFilterChange("leadState", val);
                // Reset city when state changes
                onFilterChange && onFilterChange("leadCity", "");
              }}
            />
          </Col>
          <Col className="custom_col">
            <AnimatedSelect
              multiple
              value={filterValues.leadCity || []}
              placeholder="Select Cities"
              options={city.map(item => ({ label: item.name, value: item.id }))}
              onChange={(val) => onFilterChange && onFilterChange("leadCity", val)}
            />
          </Col>
        </>
      )}

      

      {selectedFilter === "Lead Timeline" && ( subFilter === "Created On" || subFilter === "Last Updated On" || subFilter === "Converted On" ) && (
        <>
        <Col className="custom_col">
          <Form.Control 
            type="date" 
            placeholder="Start Date"
            value={filterValues.startOnDate || ""}
            onChange={(e) => onFilterChange && onFilterChange("startOnDate", e.target.value)}
          />
        </Col>
        <Col className="custom_col">
          <Form.Control 
            type="date" 
            placeholder="End Date"
            value={filterValues.endOnDate || ""}
            onChange={(e) => onFilterChange && onFilterChange("endOnDate", e.target.value)}
          />
        </Col>
        </>
      )}

     


      
    </>
  );
};

export default PartnerLeadFilters;

// Build dynamic Management filters from PartnerLeads API response
export function buildPartnerLeadsDynamicFilters(filtersFromApi = {}) {
  const nextDynamic = [];
  if (!Array.isArray(filtersFromApi.leads)) return nextDynamic;

  const leadGroups = [];
  filtersFromApi.leads.forEach((lead, idx) => {
    const operator = lead.operator || 'has done';
    const action = lead.action || '';

    const leadGroup = {
      hasDone: operator.includes('not') ? 'HasNotDone' : 'HasDone',
      mainFilter: '',
      subFilter: '',
      campaignType: '',
      landingPage: '',
      leadZone: '',
      leadState: '',
      leadCity: '',
      leadSourceValue: '',
      startOnDate: '',
      lastUpdatedDate: '',
      endOnDate: ''
    };

    switch (action) {
      case 'lead_source':
        leadGroup.mainFilter = 'Lead Source';
        if (lead.condition?.source === 'website_from') {
          leadGroup.subFilter = 'Website Form';
          leadGroup.leadSourceValue = lead.condition?.value || '';
        } else if (lead.condition?.source === 'landing_page') {
          leadGroup.subFilter = 'Landing Pages';
          leadGroup.landingPage = lead.condition?.value || '';
        } else if (lead.condition?.source === 'campaign') {
          leadGroup.subFilter = 'Campaign';
          leadGroup.campaignType = lead.condition?.value || '';
        } else if (lead.condition?.source === 'manual_entry') {
          leadGroup.subFilter = 'Manual Entry';
          leadGroup.leadSourceValue = lead.condition?.value || '';
        }
        break;
      case 'lead_status':
        leadGroup.mainFilter = 'Lead Status / Stage';
        leadGroup.subFilter = lead.condition?.values || '';
        break;
      case 'lead_demographics':
        leadGroup.mainFilter = 'Lead Demographics';
        if (lead.condition?.zone) leadGroup.leadZone = String(lead.condition.zone);
        if (lead.condition?.state) leadGroup.leadState = String(lead.condition.state);
        if (lead.condition?.city) {
          leadGroup.leadCity = Array.isArray(lead.condition.city)
            ? lead.condition.city.map(String)
            : [String(lead.condition.city)];
        }
        break;
      case 'lead_timeline':
        leadGroup.mainFilter = 'Lead Timeline';
        // Support new format: condition.timeline + condition.value
        if (lead.condition?.timeline) {
          const tl = String(lead.condition.timeline);
          const val1 = lead.condition.start_date || '';
          const val2 = lead.condition.end_date || '';
        
          if (tl === 'created_on') {
            leadGroup.subFilter = 'Created On';
          } else if (tl === 'last_updated_on') {
            leadGroup.subFilter = 'Last Updated On';
          } else if (tl === 'converted_on') {
            leadGroup.subFilter = 'Converted On';
          }
          leadGroup.startOnDate = val1;
          leadGroup.endOnDate = val2;
        }
        // Backward-compat legacy format with nested objects
        if (lead.condition?.created_on) {
          leadGroup.subFilter = 'Created On';
          leadGroup.startOnDate = lead.condition.created_on.start_date || '';
        }
        if (lead.condition?.last_updated_on) {
          leadGroup.subFilter = 'Last Updated On';
          leadGroup.lastUpdatedDate = lead.condition.last_updated_on.start_date || '';
        }
        if (lead.condition?.converted_on) {
          leadGroup.subFilter = 'Converted On';
          leadGroup.endOnDate = lead.condition.converted_on.start_date || '';
        }
        break;
      default:
        break;
    }

    leadGroups.push(leadGroup);
  });

  leadGroups.forEach((group, idx) => {
    nextDynamic.push({
      id: Date.now() + 5000 + idx,
      type: 'Management',
      name: 'Management',
      filters: group
    });
  });

  return nextDynamic;
}

// Build dynamic Management filters from PartnerLeadDefaultData array
export function buildPartnerLeadsDefaultFilters(PartnerLeadDefaultData = []) {
  const nextDynamic = [];
  if (!Array.isArray(PartnerLeadDefaultData)) return nextDynamic;

  PartnerLeadDefaultData.forEach((m, idx) => {
    const uiManagement = {
      mainFilter: m?.mainFilter || '',
      subFilter: m?.subFilter || '',
      campaignType: m?.campaignType || '',
      landingPage: m?.landingPage || '',
      leadZone: m?.leadZone || '',
      leadState: m?.leadState || '',
      leadCity: m?.leadCity || '',
      lastContactedDate: m?.lastContactedDate || '',
      followUpCount: m?.followUpCount || '',
      nextAction: m?.nextAction || '',
      timelineDate: m?.timelineDate || '',
    };
    nextDynamic.push({ id: Date.now() + 4000 + idx, type: 'Management', name: 'Management', filters: uiManagement });
  });

  return nextDynamic;
}

// Section wrapper for PartnerLeads (default form + applied filters)
export const PartnerLeadsSection = ({
  dynamicFilters = [],
  renderFilterContent,
  renderManagementFilters,
  onResetDefault,
  onAddFilter,
  showDefault = true
}) => {
  // State to track expanded section
  const [isExpanded, setIsExpanded] = useState(true);

  // Group filters by type (Management)
  const managementFilters = dynamicFilters.filter(filter => filter.type === "Management");
  const count = managementFilters.length;
  const hasDefaultFilter = showDefault && dynamicFilters.length === 0;

  return (
    <div className="mb-3 ps-3">
      <div className="filter-sections-wrapper">
        <div className="filter-section-container">
          <div 
            className="filter-section-header"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="filter-section-title">
              {isExpanded ? (
                <BsChevronDown className="chevron-icon" />
              ) : (
                <BsChevronRight className="chevron-icon" />
              )}
              <span>Partner Leads</span>
              {count > 0 && (
                <span className="filter-section-count">({count})</span>
              )}
            </div>
            <button
              className="filter-section-add-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onAddFilter) {
                  onAddFilter();
                  // Expand section when adding a new filter
                  if (!isExpanded) {
                    setIsExpanded(true);
                  }
                }
              }}
              type="button"
            >
              +
            </button>
          </div>
          {isExpanded && (
            <div className="filter-section-content">
              {/* Default Partner Leads filters section - toggled by showDefault */}
              {hasDefaultFilter && (
                <div className="filter-row">
                  <div className="row row-cols-auto text-center ms-2">
                    {renderManagementFilters && renderManagementFilters()}
                    <div className="col custom_col">
                      <button
                        type="button"
                        className="ms-2 btn btn-outline-danger btn-sm py-0 px-1"
                        onClick={onResetDefault}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Applied Filters Section */}
              {managementFilters.map((filter) => (
                <div key={filter.id} className="filter-row">
                  {renderFilterContent && renderFilterContent(filter.type, filter.id)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Process partner leads filters for payload building
export function processPartnersLeadFilters(values, payload) {
  if (!values || Object.keys(values).length === 0) return;
  const operator = values.hasDone === "HasNotDone" ? "has not done" : "has done";
  const operatorUnderscored = operator.replace(/\s+/g, "_");

  // Maps to remove duplicacy
  const LEAD_SOURCE_MAP = {
    "website form": "website_from",
    "website_from": "website_from",
    "landing pages": "landing_page",
    "campaign": "campaign",
    "manual entry": "manual_entry",
    "manual_entry": "manual_entry",
  };
  const TIMELINE_KEY_MAP = {
    "Created On": "created_on",
    "Last Updated On": "last_updated_on",
    "Converted On": "converted_on",
  };

  if (values.mainFilter === "Lead Source") {
    const src = (values.subFilter || "").toLowerCase();
    const source = LEAD_SOURCE_MAP[src] || "";

    const leadSource = {
      operator,
      action: "lead_source",
      condition: { source },
    };

    const leadSourceValueBySource = {
      landing_page: values.landingPage,
      campaign: values.campaignType,
      website_from: values.leadSourceValue,
      manual_entry: values.leadSourceValue,
    };
    const resolvedValue = leadSourceValueBySource[source];
    
    // Only add value field for sources that need it (exclude website_from)
    if (resolvedValue && source !== "website_from") {
      leadSource.condition.value = resolvedValue;
    }

    payload.filter.leads.push(leadSource);
  }

  if (values.mainFilter === "Lead Status / Stage" && values.subFilter) {
    payload.filter.leads.push({
      operator: operatorUnderscored,
      action: "lead_status",
      condition: { values: String(values.subFilter) },
    });
  }

  if (values.mainFilter === "Lead Demographics") {
    const leadDemographics = {
      operator,
      action: "lead_demographics",
      condition: {}
    };

    if (values.leadZone) {
      leadDemographics.condition.zone = Number(values.leadZone);
    }
    if (values.leadState) {
      leadDemographics.condition.state = Number(values.leadState);
    }
    if (values.leadCity && (Array.isArray(values.leadCity) ? values.leadCity.length > 0 : true)) {
      const cityArray = Array.isArray(values.leadCity) ? values.leadCity : [values.leadCity];
      leadDemographics.condition.city = cityArray.map((c) => Number(c));
    }

    // Only add to payload if at least one demographic field is selected
    if (Object.keys(leadDemographics.condition).length > 0) {
      payload.filter.leads.push(leadDemographics);
    }
  }

  if (values.mainFilter === "Lead Timeline") {
    const timelineKey = TIMELINE_KEY_MAP[values.subFilter] || "";
    const startDateValue = values.startOnDate || "";
    const endDateValue = values.endOnDate || "";

    if (timelineKey && (startDateValue || endDateValue)) {
      const condition = {
        timeline: timelineKey
      };
      
      if (startDateValue) {
        condition.start_date = startDateValue;
      }
      if (endDateValue) {
        condition.end_date = endDateValue;
      }

      payload.filter.leads.push({
        operator,
        action: "lead_timeline",
        condition
      });
    }
  }

  
}